import type {
  ISDKAdapterSetOptions,
  ISettingsParam,
  ISettingsValue,
  IStyleParam,
  IStyleParamValuesByType,
  SettingsParamType,
  StyleParamType,
} from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { useExperiments } from '@wix/yoshi-flow-editor';

import { Experiment } from '../../../types';

export const useExperimentalCascadingSettings = () => {
  const settings = useSettings();
  const styles = useStyles();
  const { experiments } = useExperiments();

  const isExperimentEnabled = experiments.enabled(
    Experiment.UseMigratedEditor3StylesParams,
  );

  return {
    get: <T extends SettingsParamType, U extends StyleParamType>(
      settingsParam: ISettingsParam<T>,
      styleParam: IStyleParam<U>,
    ) => {
      const valueInSettings = settings.get(settingsParam);
      const valueInStyles = styles.get(styleParam);

      return isExperimentEnabled
        ? valueInStyles ?? valueInSettings
        : valueInSettings;
    },
    set: <T extends StyleParamType, K extends SettingsParamType>(
      settingsParam: ISettingsParam<K>,
      stylesParam: IStyleParam<T>,
      value: IStyleParamValuesByType[T] | ISettingsValue[K],
      options?: Partial<ISDKAdapterSetOptions>,
    ) => {
      if (!isExperimentEnabled) {
        return settings.set(settingsParam, value);
      }
      return styles.set(stylesParam, value, {
        isGlobal: options?.isGlobal,
      });
    },
  };
};

export const useCascadingSettings = () => {
  const settings = useSettings();
  const styles = useStyles();

  return {
    get: <T extends SettingsParamType, U extends StyleParamType>(
      settingsParam: ISettingsParam<T>,
      styleParam: IStyleParam<U>,
    ) => {
      const valueInSettings = settings.get(settingsParam);
      const valueInStyles = styles.get(styleParam);

      return valueInStyles ?? valueInSettings;
    },
    set: <T extends StyleParamType>(
      stylesParam: IStyleParam<T>,
      value: IStyleParamValuesByType[T],
      options?: Partial<ISDKAdapterSetOptions>,
    ) => {
      return styles.set(stylesParam, value, { isGlobal: options?.isGlobal });
    },
  };
};
